import React, { useState, useEffect } from 'react';
import {
  AppBar,
  Toolbar,
  Typography,
  Box,
  IconButton,
  Menu,
  MenuItem,
  Button,
  Select,
  FormControl,
  SelectChangeEvent,
  Divider,
  CircularProgress,
  ListItemIcon,
  ListItemText,
  Avatar,
} from '@mui/material';
import {
  AccountCircle,
  Language as LanguageIcon,
  AdminPanelSettings as AdminIcon,
  Add as AddIcon,
  Dashboard as DashboardIcon,
  Widgets as ComponentsIcon,
  Warning as RisksIcon,
  Assessment as ReportsIcon,
  Public as PublicIcon,
  Lock as PrivateIcon,
  Person as PersonIcon,
  Settings as SettingsIcon,
  Logout as LogoutIcon,
} from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { AppSwitcher } from '@silverdream/omni-tek-shared-ui';
import { useSpace } from '../../contexts/SpaceContext';
import { useAuth } from '../../contexts/AuthContext';
import { spacesService } from '../../services/spaces.service';
import type { Space } from 'shared';
import omniTekLogo from '../../assets/omni-tek-full-logo-tp.png';

export const Header: React.FC = () => {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const { currentSpace, setCurrentSpace, savedSpaceId } = useSpace();
  const { user, isAdmin, logout, isAuthenticated } = useAuth();

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const [anchorElUser, setAnchorElUser] = useState<any>(null);
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const [anchorElCreate, setAnchorElCreate] = useState<any>(null);

  // Fetch accessible spaces from API
  const { data: spaces = [], isLoading: spacesLoading } = useQuery<Space[]>({
    queryKey: ['spaces', 'accessible'],
    queryFn: () => spacesService.getAccessible(),
  });

  // Restore space selection from localStorage after spaces load
  useEffect(() => {
    if (savedSpaceId && spaces.length > 0 && !currentSpace) {
      const savedSpace = spaces.find(p => p.id === savedSpaceId);
      if (savedSpace) {
        setCurrentSpace(savedSpace);
      }
    }
  }, [savedSpaceId, spaces, currentSpace, setCurrentSpace]);

  const handleUserMenuOpen = (event: React.MouseEvent) => {
    setAnchorElUser(event.currentTarget);
  };

  const handleUserMenuClose = () => {
    setAnchorElUser(null);
  };

  const handleSpaceChange = (event: SelectChangeEvent<string>) => {
    const spaceId = event.target.value;

    // Handle "Create new space" option
    if (spaceId === 'create') {
      navigate('/spaces?action=create');
      return;
    }

    // Handle "Browse all spaces" option
    if (spaceId === 'browse') {
      navigate('/spaces');
      return;
    }

    const space = spaces.find(p => p.id === spaceId);
    if (space) {
      setCurrentSpace(space);
      // Navigate to the space detail page
      navigate(`/spaces/${space.id}`);
    }
  };

  const handleLanguageToggle = () => {
    const newLang = i18n.language === 'nb' ? 'en' : 'nb';
    i18n.changeLanguage(newLang);
  };

  const handleCreateMenuOpen = (event: React.MouseEvent) => {
    setAnchorElCreate(event.currentTarget);
  };

  const handleCreateMenuClose = () => {
    setAnchorElCreate(null);
  };

  const handleCreateComponent = () => {
    handleCreateMenuClose();
    if (currentSpace?.id) {
      navigate(`/spaces/${currentSpace.id}/components?action=create`);
    } else {
      navigate('/components?action=create');
    }
  };

  const handleCreateRisk = () => {
    handleCreateMenuClose();
    navigate('/risks?action=create');
  };

  const handleCreateMitigation = () => {
    handleCreateMenuClose();
    navigate('/mitigations?action=create');
  };

  const handleCreateDiagram = () => {
    handleCreateMenuClose();
    if (currentSpace?.id) {
      navigate(`/spaces/${currentSpace.id}?action=create-diagram`);
    } else {
      navigate('/spaces');
    }
  };

  const handleLogout = async () => {
    handleUserMenuClose();
    await logout();
    // Note: In OIDC mode, logout() redirects to Authentik for Single Logout
    // navigate() is only needed for local auth mode (redirect handled in logout)
  };

  return (
    <AppBar position="sticky" elevation={1} sx={{ bgcolor: '#2D3B45' }}>
      <Toolbar>
        {/* App Switcher (Hamburger Menu) */}
        <AppSwitcher currentAppId="risk-management" />

        {/* Logo and Title */}
        <Box sx={{ display: 'flex', alignItems: 'center', mr: 4 }}>
          <Box
            component="img"
            src={omniTekLogo}
            alt="Omni-Tek"
            sx={{
              height: 32,
              mr: 2,
            }}
          />
          <Typography
            variant="h6"
            component="div"
            sx={{ fontWeight: 'bold', color: 'white', cursor: 'pointer' }}
            onClick={() => navigate('/')}
          >
            {t('app.shortTitle')}
          </Typography>
        </Box>
        {/* Space Selector - Primary Navigation Element */}
        <FormControl size="small" sx={{ minWidth: 220, mr: 3 }}>
          <Select
            value={currentSpace?.id || ''}
            onChange={handleSpaceChange}
            displayEmpty
            disabled={spacesLoading}
            sx={{ bgcolor: 'background.paper' }}
            renderValue={selected => {
              if (!selected) {
                return <em>{t('space.selectSpace')}</em>;
              }
              const space = spaces.find(s => s.id === selected);
              if (!space) return selected;
              return (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  {space.isPublic ? (
                    <PublicIcon fontSize="small" color="action" />
                  ) : (
                    <PrivateIcon fontSize="small" color="action" />
                  )}
                  {space.name}
                </Box>
              );
            }}
          >
            <MenuItem value="" disabled>
              <em>
                {spacesLoading ? (
                  <>
                    <CircularProgress size={16} sx={{ mr: 1 }} />
                    {t('common.loading')}
                  </>
                ) : (
                  t('space.selectSpace')
                )}
              </em>
            </MenuItem>
            {spaces.map(space => (
              <MenuItem key={space.id} value={space.id}>
                <Box
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 1,
                    width: '100%',
                  }}
                >
                  {space.isPublic ? (
                    <PublicIcon fontSize="small" color="action" />
                  ) : (
                    <PrivateIcon fontSize="small" color="action" />
                  )}
                  {space.name}
                </Box>
              </MenuItem>
            ))}
            <Divider />
            <MenuItem value="create" sx={{ fontStyle: 'italic' }}>
              <AddIcon fontSize="small" sx={{ mr: 1 }} />
              {t('space.createNew')}
            </MenuItem>
            <MenuItem value="browse" sx={{ fontStyle: 'italic' }}>
              {t('space.browseAll')}
            </MenuItem>
          </Select>
        </FormControl>
        {/* Space Settings Button - Only visible when a space is selected */}
        {currentSpace && (
          <IconButton
            color="inherit"
            onClick={() => navigate(`/spaces/${currentSpace.id}/settings`)}
            sx={{
              mr: 2,
              bgcolor: 'rgba(255, 255, 255, 0.1)',
              '&:hover': {
                bgcolor: 'rgba(255, 255, 255, 0.2)',
              },
            }}
            title={t('space.settings')}
          >
            <SettingsIcon />
          </IconButton>
        )}
        {/* Create Dropdown Button */}
        <Button
          color="inherit"
          onClick={handleCreateMenuOpen}
          startIcon={<AddIcon />}
          sx={{
            mr: 2,
            bgcolor: 'rgba(255, 255, 255, 0.1)',
            '&:hover': {
              bgcolor: 'rgba(255, 255, 255, 0.2)',
            },
          }}
        >
          {t('common.create')}
        </Button>
        <Menu
          anchorEl={anchorElCreate}
          open={Boolean(anchorElCreate)}
          onClose={handleCreateMenuClose}
          anchorOrigin={{
            vertical: 'bottom',
            horizontal: 'left',
          }}
          transformOrigin={{
            vertical: 'top',
            horizontal: 'left',
          }}
          PaperProps={{
            sx: {
              width: 280,
              mt: 1,
            },
          }}
        >
          <MenuItem onClick={handleCreateComponent} sx={{ py: 1.5 }}>
            <ComponentsIcon sx={{ mr: 2, color: 'primary.main' }} />
            <Box>
              <Typography variant="body1">{t('component.create')}</Typography>
              <Typography variant="caption" color="text.secondary">
                {t('component.createDescription')}
              </Typography>
            </Box>
          </MenuItem>
          <MenuItem onClick={handleCreateDiagram} sx={{ py: 1.5 }}>
            <DashboardIcon sx={{ mr: 2, color: 'primary.main' }} />
            <Box>
              <Typography variant="body1">{t('diagram.create')}</Typography>
              <Typography variant="caption" color="text.secondary">
                {t('diagram.createDescription')}
              </Typography>
            </Box>
          </MenuItem>
          <MenuItem onClick={handleCreateRisk} sx={{ py: 1.5 }}>
            <RisksIcon sx={{ mr: 2, color: 'warning.main' }} />
            <Box>
              <Typography variant="body1">{t('risk.create')}</Typography>
              <Typography variant="caption" color="text.secondary">
                {t('risk.createDescription')}
              </Typography>
            </Box>
          </MenuItem>
          <MenuItem onClick={handleCreateMitigation} sx={{ py: 1.5 }}>
            <ReportsIcon sx={{ mr: 2, color: 'success.main' }} />
            <Box>
              <Typography variant="body1">{t('mitigation.create')}</Typography>
              <Typography variant="caption" color="text.secondary">
                {t('mitigation.createDescription')}
              </Typography>
            </Box>
          </MenuItem>
        </Menu>

        {/* Navigation Links */}
        <Box sx={{ flexGrow: 1, display: 'flex', gap: 2 }}>
          <Button color="inherit" onClick={() => navigate('/')}>
            {t('nav.dashboard')}
          </Button>
          <Button color="inherit" onClick={() => navigate('/diagrams')}>
            {t('nav.diagrams')}
          </Button>
          <Button color="inherit" onClick={() => navigate('/components')}>
            {t('nav.components')}
          </Button>
          <Button color="inherit" onClick={() => navigate('/risks')}>
            {t('nav.risks')}
          </Button>
          <Button color="inherit" onClick={() => navigate('/reports')}>
            {t('nav.reports')}
          </Button>
        </Box>
        {/* Admin Button - Only visible to admin users */}
        {isAdmin && (
          <IconButton
            color="inherit"
            onClick={() => navigate('/admin')}
            sx={{ mr: 1 }}
            title={t('nav.admin')}
          >
            <AdminIcon />
          </IconButton>
        )}
        {/* Language Toggle */}
        <IconButton
          color="inherit"
          onClick={handleLanguageToggle}
          sx={{ mr: 1 }}
          title={t('user.language')}
        >
          <LanguageIcon />
          <Typography variant="caption" sx={{ ml: 0.5 }}>
            {i18n.language.toUpperCase()}
          </Typography>
        </IconButton>
        {/* User Menu */}
        {isAuthenticated && user ? (
          <>
            <IconButton
              color="inherit"
              onClick={handleUserMenuOpen}
              title={t('user.profile')}
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 1,
                borderRadius: 2,
                px: 1,
              }}
            >
              <Avatar
                sx={{
                  width: 32,
                  height: 32,
                  bgcolor: 'secondary.main',
                  fontSize: '0.875rem',
                }}
              >
                {user.name?.charAt(0).toUpperCase() || 'U'}
              </Avatar>
              <Typography
                variant="body2"
                sx={{
                  display: { xs: 'none', md: 'block' },
                  maxWidth: 120,
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap',
                }}
              >
                {user.name}
              </Typography>
            </IconButton>
            <Menu
              anchorEl={anchorElUser}
              open={Boolean(anchorElUser)}
              onClose={handleUserMenuClose}
              anchorOrigin={{
                vertical: 'bottom',
                horizontal: 'right',
              }}
              transformOrigin={{
                vertical: 'top',
                horizontal: 'right',
              }}
              PaperProps={{
                sx: { minWidth: 200, mt: 1 },
              }}
            >
              {/* User info header */}
              <Box
                sx={{ px: 2, py: 1.5, borderBottom: 1, borderColor: 'divider' }}
              >
                <Typography variant="subtitle2" noWrap>
                  {user.name}
                </Typography>
                <Typography variant="caption" color="text.secondary" noWrap>
                  {user.email}
                </Typography>
              </Box>
              <MenuItem
                onClick={() => {
                  handleUserMenuClose();
                  navigate('/profile');
                }}
              >
                <ListItemIcon>
                  <PersonIcon fontSize="small" />
                </ListItemIcon>
                <ListItemText>{t('user.profile')}</ListItemText>
              </MenuItem>
              <MenuItem
                onClick={() => {
                  handleUserMenuClose();
                  navigate('/settings');
                }}
              >
                <ListItemIcon>
                  <SettingsIcon fontSize="small" />
                </ListItemIcon>
                <ListItemText>{t('user.settings')}</ListItemText>
              </MenuItem>
              <Divider />
              <MenuItem onClick={handleLogout}>
                <ListItemIcon>
                  <LogoutIcon fontSize="small" />
                </ListItemIcon>
                <ListItemText>{t('user.logout')}</ListItemText>
              </MenuItem>
            </Menu>
          </>
        ) : (
          <Button
            color="inherit"
            onClick={() => navigate('/login')}
            startIcon={<AccountCircle />}
          >
            {t('auth.login')}
          </Button>
        )}
      </Toolbar>
    </AppBar>
  );
};
